import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Minus, Plus, ReceiptText } from "lucide-react";
import { Button, EmptyState, SmartImage, SwagCard } from "@ec2/ui";
import { useT } from "@ec2/i18n/react";
import type { EventSwagView } from "@ec2/contracts";
import { useTRPC } from "../lib/api";
import { appConfig } from "../lib/config";
import { generalError, type LocalizedError } from "../lib/errors";
import { useActive } from "../state/hooks";
import { useSession } from "../state/session";
import { Shell } from "../components/Shell";
import { Sheet } from "../components/Sheet";
import { ErrorLine, LoadingLine } from "../components/common";

/** Shop (brief §6): 2-up swag grid, checkout sheet with balance-after line. */
export function ShopScreen() {
  const t = useT();
  const navigate = useNavigate();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { locale } = useSession();
  const { active, me } = useActive();
  const shop = useQuery({
    ...trpc.shop.list.queryOptions({ eventId: active?.eventId ?? "" }),
    enabled: Boolean(active),
    refetchInterval: 30_000,
  });
  const order = useMutation(trpc.shop.order.mutationOptions());
  const [selected, setSelected] = useState<EventSwagView | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [error, setError] = useState<LocalizedError | null>(null);

  const balance = active?.bucksBalance ?? 0;
  const items = (shop.data ?? []).filter((item) => item.active);

  const openCheckout = (item: EventSwagView) => {
    setSelected(item);
    setQuantity(1);
    setError(null);
  };

  const placeOrder = async () => {
    if (!selected || !active) return;
    setError(null);
    try {
      const result = await order.mutateAsync({
        eventId: active.eventId,
        items: [{ eventSwagId: selected.id, quantity }],
      });
      if (!result.orderId) {
        setError({ key: "ec2.shop.someUnavailable" });
        void shop.refetch();
        return;
      }
      await queryClient.invalidateQueries();
      void me.refetch();
      void navigate({ to: "/orders/$orderId", params: { orderId: result.orderId } });
    } catch (err) {
      setError(generalError(err));
    }
  };

  const total = selected ? selected.priceBucks * quantity : 0;
  const maxQuantity = selected ? Math.max(1, Math.min(selected.stockAvailable, 99)) : 1;

  return (
    <Shell active="shop">
      <header className="flex items-center justify-between px-4 pb-3 pt-4">
        <h1 className="font-display text-[30px] leading-[1.1] font-bold tracking-[-0.02em] text-fg">{t("ec2.shop.title")}</h1>
        <button
          type="button"
          onClick={() => void navigate({ to: "/orders" })}
          data-testid="ec-shop-orders"
          className="flex items-center gap-1.5 rounded-(--radius-chip) px-3 py-2 text-[13px] font-semibold text-fg-secondary focus-visible:outline-2 focus-visible:outline-primary"
        >
          <ReceiptText size={18} aria-hidden />
          {t("ec2.shop.viewOrders")}
        </button>
      </header>

      {/* SwagCard renders h3 titles — keep the heading order valid for AT. */}
      <h2 className="sr-only">{t("ec2.shop.title")}</h2>
      <div className="grid grid-cols-2 gap-3 px-4" data-testid="ec-shop-grid">
        {shop.isLoading && <LoadingLine className="col-span-2" />}
        {shop.data && items.length === 0 && (
          <div className="col-span-2">
            <EmptyState title={t("ec2.shop.empty.title")} body={t("ec2.shop.empty.body")} accentClassName="text-bucks" />
          </div>
        )}
        {items.map((item) => {
          const soldOut = item.stockAvailable <= 0;
          const short = balance < item.priceBucks;
          return (
            <SwagCard
              key={item.id}
              image={
                item.imageKey ? (
                  <SmartImage base={appConfig.mediaBase} mediaKey={item.imageKey} alt={item.name} width={240} height={240} />
                ) : (
                  <span aria-hidden className="flex h-full w-full items-center justify-center font-display text-3xl text-fg-muted">?</span>
                )
              }
              name={item.name}
              price={item.priceBucks}
              priceLabel={t("ec2.a11y.bucksReward", { amount: item.priceBucks })}
              stockCaption={
                soldOut
                  ? t("ec2.shop.soldOut")
                  : item.stockAvailable <= 5
                    ? t("ec2.shop.stockLow", { count: item.stockAvailable })
                    : undefined
              }
              insufficientCaption={!soldOut && short ? t("ec2.shop.needMore", { amount: item.priceBucks - balance }) : undefined}
              onClick={soldOut ? undefined : () => openCheckout(item)}
              locale={locale}
            />
          );
        })}
      </div>

      {selected && (
        <Sheet label={selected.name} onClose={() => setSelected(null)}>
          <div className="flex flex-col gap-4" data-testid="ec-checkout">
            <h2 className="pr-8 font-display text-[21px] font-[650] text-fg">{selected.name}</h2>
            {selected.description && <p className="text-base text-fg-secondary">{selected.description}</p>}
            <div className="flex items-center justify-between">
              <span className="text-[13px] font-medium text-fg-secondary">{t("ec2.shop.quantity")}</span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  aria-label={t("ec2.shop.quantity")}
                  data-testid="ec-qty-minus"
                  className="flex size-10 items-center justify-center rounded-full bg-surface-overlay text-fg focus-visible:outline-2 focus-visible:outline-primary"
                >
                  <Minus size={18} aria-hidden />
                </button>
                <span className="w-6 text-center font-display text-lg font-[650] text-fg tabular-nums" data-testid="ec-qty">
                  {quantity}
                </span>
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.min(maxQuantity, q + 1))}
                  aria-label={t("ec2.shop.quantity")}
                  data-testid="ec-qty-plus"
                  className="flex size-10 items-center justify-center rounded-full bg-surface-overlay text-fg focus-visible:outline-2 focus-visible:outline-primary"
                >
                  <Plus size={18} aria-hidden />
                </button>
              </div>
            </div>
            <p className="text-[13px] font-medium text-fg-secondary tabular-nums" data-testid="ec-balance-after">
              {t("ec2.shop.balanceAfter", {
                before: new Intl.NumberFormat(locale).format(balance),
                after: new Intl.NumberFormat(locale).format(balance - total),
              })}
            </p>
            <ErrorLine error={error} />
            <Button
              variant="primary"
              disabled={order.isPending || balance < total}
              onClick={() => void placeOrder()}
              data-testid="ec-order-cta"
            >
              {t("ec2.shop.orderFor", { amount: new Intl.NumberFormat(locale).format(total) })}
            </Button>
          </div>
        </Sheet>
      )}
    </Shell>
  );
}
